/**
 * SendGrid Analytics Page
 *
 * Email delivery analytics including delivery rates, bounce rates,
 * trends, email type breakdown, and domain deliverability.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import PeriodSelector from '../components/analytics/PeriodSelector';
import MetricCard from '../components/analytics/MetricCard';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import DistributionChart from '../components/founder-dashboard/DistributionChart';
import {
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EnvelopeOpenIcon,
  CursorArrowRaysIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

export default function SendGridPage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [byEmailType, setByEmailType] = useState(null);
  const [byDomain, setByDomain] = useState(null);
  const [trends, setTrends] = useState(null);
  const [suppressions, setSuppressions] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, byTypeRes, byDomainRes, trendsRes, suppressionsRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getSendGridOverview(period),
        thirdPartyAnalyticsAPI.getSendGridByEmailType(period),
        thirdPartyAnalyticsAPI.getSendGridByDomain(period),
        thirdPartyAnalyticsAPI.getSendGridTrends(period),
        thirdPartyAnalyticsAPI.getSendGridSuppressions(),
      ]);
      setOverview(overviewRes.data);
      setByEmailType(byTypeRes.data);
      setByDomain(byDomainRes.data);
      setTrends(trendsRes.data);
      setSuppressions(suppressionsRes.data);
    } catch (err) {
      console.error('[SendGridPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv, period]);

  // Determine status based on thresholds
  const getBounceStatus = () => {
    const rate = overview?.bounce_rate || 0;
    if (rate > 5) return 'critical';
    if (rate > 3) return 'warning';
    return 'healthy';
  };

  const getSpamStatus = () => {
    const rate = overview?.spam_rate || 0;
    if (rate > 0.5) return 'critical';
    if (rate > 0.1) return 'warning';
    return 'healthy';
  };

  const getDeliveryStatus = () => {
    const rate = overview?.delivery_rate || 100;
    if (rate < 90) return 'critical';
    if (rate < 95) return 'warning';
    return 'healthy';
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="SendGrid Analytics" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading SendGrid analytics</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare trend chart data
  const trendData = trends?.data || [];
  const trendLines = [
    { key: 'delivered', name: 'Delivered', color: '#22C55E' },
    { key: 'bounces', name: 'Bounced', color: '#EF4444' },
  ];

  // Prepare email type breakdown for chart
  const emailTypeDistribution = {};
  (byEmailType?.emailTypes || []).forEach((t) => {
    emailTypeDistribution[t.email_type] = t.delivery_rate / 100;
  });

  // Domain table columns - backend returns recipient_domain, delivered, bounces, delivery_rate
  const domainColumns = [
    { key: 'recipient_domain', header: 'Domain', sortable: true },
    {
      key: 'delivery_rate',
      header: 'Delivery Rate',
      sortable: true,
      render: (val, row) => {
        const rate = parseFloat(val) || 0;
        const isLow = rate < 95;
        return (
          <span className={isLow ? 'text-yellow-600 font-medium' : ''}>
            {isLow && '\u26A0\uFE0F '}{rate.toFixed(1)}%
          </span>
        );
      },
    },
    {
      key: 'sent',
      header: 'Sent',
      sortable: true,
      render: (val, row) => String((parseInt(row.delivered) || 0) + (parseInt(row.bounces) || 0)),
    },
    { key: 'delivered', header: 'Delivered', sortable: true },
    { key: 'bounces', header: 'Bounced', sortable: true },
  ];

  // Suppressions table columns - backend returns 'created' not 'created_at'
  const suppressionColumns = [
    { key: 'email', header: 'Email', sortable: true },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (val) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          val === 'bounce' ? 'bg-red-100 text-red-800' :
          val === 'spam' ? 'bg-orange-100 text-orange-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {val}
        </span>
      ),
    },
    { key: 'reason', header: 'Reason', sortable: false },
    {
      key: 'created',
      header: 'Date',
      sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString() : '-',
    },
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="SendGrid Analytics"
        description="Email delivery performance and health metrics"
        onRefresh={fetchData}
        loading={loading}
      >
        <PeriodSelector value={period} onChange={setPeriod} />
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard
          title="Total Sent"
          value={overview?.total_requests}
          format="number"
          icon={PaperAirplaneIcon}
          change={overview?.delivery_change}
          loading={loading}
        />
        <MetricCard
          title="Delivery Rate"
          value={overview?.delivery_rate}
          format="percent"
          icon={EnvelopeIcon}
          status={getDeliveryStatus()}
          loading={loading}
        />
        <MetricCard
          title="Bounce Rate"
          value={overview?.bounce_rate}
          format="percent"
          icon={ExclamationTriangleIcon}
          status={getBounceStatus()}
          loading={loading}
        />
        <MetricCard
          title="Open Rate"
          value={overview?.open_rate}
          format="percent"
          icon={EnvelopeOpenIcon}
          change={overview?.open_change}
          loading={loading}
        />
        <MetricCard
          title="Click Rate"
          value={overview?.click_rate}
          format="percent"
          icon={CursorArrowRaysIcon}
          change={overview?.click_change}
          loading={loading}
        />
        <MetricCard
          title="Spam Rate"
          value={overview?.spam_rate}
          format="percent"
          icon={NoSymbolIcon}
          status={getSpamStatus()}
          loading={loading}
        />
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Delivery Trend ({period})
        </h3>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <TrendChart
            data={trendData}
            lines={trendLines}
            height={250}
            xAxisKey="stat_date"
            type="area"
          />
        )}
      </div>

      {/* By Email Type + By Domain */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Email Type */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Delivery Rate by Email Type
          </h3>
          {loading ? (
            <div className="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={emailTypeDistribution}
              height={250}
              showPercentage
            />
          )}
        </div>

        {/* By Domain */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Domain Deliverability (Top 20)
          </h3>
          <div className="max-h-64 overflow-y-auto">
            <DataTable
              data={byDomain?.domains || []}
              columns={domainColumns}
              loading={loading}
              emptyMessage="No domain data available"
            />
          </div>
        </div>
      </div>

      {/* Suppressions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Suppressions
        </h3>
        <div className="flex items-center gap-4 mb-4 text-sm">
          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full">
            Bounces: {suppressions?.bounces?.length || 0}
          </span>
          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full">
            Spam Reports: {suppressions?.spamReports?.length || 0}
          </span>
          <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full">
            Unsubscribes: {suppressions?.unsubscribes?.length || 0}
          </span>
        </div>
        <DataTable
          data={[
            ...(suppressions?.bounces || []).map((b) => ({ ...b, type: 'bounce' })),
            ...(suppressions?.spamReports || []).map((s) => ({ ...s, type: 'spam' })),
            ...(suppressions?.unsubscribes || []).map((u) => ({ ...u, type: 'unsubscribe' })),
          ].slice(0, 20)}
          columns={suppressionColumns}
          loading={loading}
          emptyMessage="No recent suppressions"
        />
      </div>
    </div>
  );
}
